import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { Camera } from '@capacitor/camera';

// --- IMPORTAÇÕES DE TELAS ---
import LandingPage from './screens/LandingPage';
import LoginScreen from './screens/LoginScreen';
import SignUpScreen from './screens/SignUpScreen';
import MainLayout from './screens/MainLayout';
import SuperAdminScreen from './screens/SuperAdminScreen';
import SuperAdminLoginScreen from './screens/SuperAdminLoginScreen';

// --- SERVIÇO DE API (Para futuras validações de token) ---
import api from './services/api';

const App = () => {
    const [view, setView] = useState(Capacitor.isNativePlatform() ? 'login' : 'landing');
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    // --- 1. LÓGICA DE INICIALIZAÇÃO E AUTH LOCAL ---
    useEffect(() => {
        const checkLogin = async () => {
            console.log("[App] Verificando sessão local...");

            // Busca dados salvos pelo LoginScreen
            const storedToken = localStorage.getItem('token');
            const storedUser = localStorage.getItem('user');

            if (storedToken && storedUser) {
                try {
                    const parsedUser = JSON.parse(storedUser);
                    setUser(parsedUser);
                    console.log("[App] Usuário restaurado:", parsedUser.email);

                    // (Opcional) Aqui você poderia validar o token com o backend:
                    // await api.get('/auth/verify'); 

                } catch (e) {
                    console.error("[App] Erro ao ler dados do usuário. Sessão corrompida.");
                    handleLogout(); // Limpa tudo se estiver quebrado
                }
            } else {
                console.log("[App] Nenhuma sessão encontrada.");
                setUser(null);
            }

            setLoading(false);
        };

        checkLogin();

        // Inicializa permissões mobile (se estiver no celular)
        if (Capacitor.isNativePlatform()) {
            initializeMobilePermissions();
        }

    }, []);

    // --- 2. PERMISSÕES MOBILE (Câmera e Push) ---
    const initializeMobilePermissions = async () => {
        try {
            // A) Câmera
            try {
                await Camera.requestPermissions({ permissions: ['camera', 'photos'] });
            } catch (e) { console.error("Erro permissão câmera:", e); }

            // B) Push Notifications
            let permStatus = await PushNotifications.checkPermissions();
            if (permStatus.receive === 'prompt') {
                permStatus = await PushNotifications.requestPermissions();
            }

            if (permStatus.receive === 'granted') {
                // Cria canal de notificação para Android
                await PushNotifications.createChannel({
                    id: 'wa_desk_notifications',
                    name: 'Notificações de Mensagem',
                    description: 'Avisos de novas mensagens',
                    importance: 5,
                    visibility: 1,
                    vibration: true,
                    sound: 'default'
                });

                // Registra no FCM
                await PushNotifications.register();
            }
        } catch (error) {
            console.error("Erro mobile init:", error);
        }
    };

    // Listeners de Push (Token e Recebimento)
    useEffect(() => {
        if (Capacitor.isNativePlatform()) {
            PushNotifications.addListener('registration', async (token) => {
                console.log('Push Token:', token.value);
                localStorage.setItem('device_push_token', token.value);

                // TODO: FUTURO - Enviar esse token para o seu Backend MongoDB
                // if (user) {
                //    await api.post('/users/push-token', { token: token.value });
                // }
            });

            PushNotifications.addListener('pushNotificationReceived', (notification) => {
                console.log('Notificação recebida:', notification);
            });
        }
    }, [user]); // Recria se o user mudar (para associar o token ao user correto no futuro)


    // --- 3. AÇÕES ---
    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        setView('landing');
        // Força recarregamento para limpar estados de memória (chats, sockets, etc)
        window.location.reload();
    };

    // --- 4. RENDERIZAÇÃO ---

    if (loading) {
        return (
            <div className="flex h-screen items-center justify-center bg-gray-50 flex-col font-sans">
                <Loader2 className="w-10 h-10 text-blue-600 animate-spin mb-4" />
                <p className="text-gray-600 font-medium">Carregando sistema...</p>
            </div>
        );
    }

    // --- LÓGICA DE ROTAS CUSTOMIZADA ---
    const path = window.location.pathname;

    // Rota Exclusiva do Super Admin
    if (path === '/superadmin') {
        if (user && user.role === 'superadmin') {
            // Se já estiver logado e for superadmin, mostra o painel e passa um handleLogout customizado que redireciona pra cá 
            return <SuperAdminScreen onLogout={() => {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                window.location.href = '/superadmin';
            }} />;
        } else {
            // Se não estiver logado, ou não for admin (forçamos ver a tela de login)
            return <SuperAdminLoginScreen />;
        }
    }

    // --- ROTAS PADRÃO (Para o resto do site) ---
    // Se tem usuário logado, vai direto pro MainLayout (Dashboard)
    if (user) {
        return <MainLayout currentUser={user} onLogout={handleLogout} />;
    }

    // Se não tem usuário, gerencia as telas de entrada
    switch (view) {
        case 'login':
            return <LoginScreen />; // LoginScreen agora já salva e dá reload, reiniciando o App como logado
        case 'signup':
            return <SignUpScreen setView={setView} />;
        case 'landing':
        default:
            return <LandingPage navigateToLogin={() => setView('login')} navigateToRegister={() => setView('signup')} />;
    }
};

export default App;